// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import './styles/main.css';

import {
  buildRegressionCsv,
  buildRegressionsJsonl,
  buildReportHtml,
  buildReportMarkdown,
  buildDiff,
  VERSION,
  type ExportContext,
} from './artefacts';
import { LIMITS_COPY, PRIVACY_NOT_PROTECTED, PRIVACY_PROTECTED, PRIVACY_TRUST } from './copy';
import { EventLog } from './eventlog';
import { initGlossary } from './glossary';
import { renderHeadline, type RunResult } from './pipeline';
import type { AlphabetEntry } from './traj/summarise';
import { SUSPICIOUS_DROP_KEYS } from './traj/normalise';
import {
  DEFAULT_RULES,
  DEFAULT_THRESHOLDS,
  type CaseResult,
  type Config,
  type FieldMap,
  type NormaliseRules,
  type Summary,
  type Verdict,
} from './types';
import { createDrawer, createModalHost, el, formatCount, renderCopy, toast } from './ui/shell';

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

/* ── Capability gate ────────────────────────────────────────────────────── */

function unsupported(reason: string): void {
  document.body.replaceChildren(
    el('div', { style: 'max-width:44rem;margin:12vh auto;padding:0 1.5rem;font-family:system-ui' }, [
      el('h1', {}, ['Backslide cannot run in this browser']),
      el('p', {}, [`It needs ${reason}. The floor is Chrome 92, Firefox 114, Safari 16.4 or iOS 16.4.`]),
      el('p', {}, ['Nothing was read from your device.']),
    ]),
  );
}

/* ── State ──────────────────────────────────────────────────────────────── */

interface SideState {
  name: string;
  rows: number;
  dialectLabel: string;
  loaded: boolean;
}

const state = {
  a: { name: '', rows: 0, dialectLabel: '', loaded: false } as SideState,
  b: { name: '', rows: 0, dialectLabel: '', loaded: false } as SideState,
  result: null as RunResult | null,
  alphabet: [] as AlphabetEntry[],
  hasTrajectories: false,
  dialectLabel: '',
  cursor: -1,
  expanded: new Set<string>(),
  config: {
    rules: { ...DEFAULT_RULES, dropKeys: [...DEFAULT_RULES.dropKeys] },
    thresholds: { ...DEFAULT_THRESHOLDS },
    schema: null,
    enabled: {},
    fanoutFilter: null,
    fieldMap: null,
  } as Config,
};

let log: EventLog;
let worker: Worker;
let modal: ReturnType<typeof createModalHost>;

/* ── Verdict presentation ───────────────────────────────────────────────── */

const VERDICT_LABEL: Readonly<Record<Verdict, string>> = {
  regression: 'worse',
  errored: 'errored',
  diverged: 'diverged',
  'diverged-args': 'args changed',
  fixed: 'better',
  unchanged: 'unchanged',
  dropped: 'dropped',
  added: 'added',
  unmatched: 'unmatched',
  'ambiguous-group': 'ambiguous',
};

const INTERESTING: readonly Verdict[] = [
  'regression',
  'errored',
  'diverged',
  'diverged-args',
  'fixed',
  'dropped',
  'added',
  'ambiguous-group',
];

/* ── Boot ───────────────────────────────────────────────────────────────── */

function boot(): void {
  if (!('Worker' in self)) return unsupported('Web Workers');

  log = new EventLog($<HTMLOListElement>('drawer-list'));
  modal = createModalHost($('modal-host'));
  createDrawer($('drawer'), $<HTMLButtonElement>('log-toggle'), $<HTMLButtonElement>('log-close'));
  initGlossary($('tooltip'));

  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = onWorkerMessage;
  worker.onerror = (e) => showError(`Something failed in the background: ${e.message}`);

  renderLimits();
  wireModals();
  wireTheme();
  wireDropzones();
  wireSamples();
  wireSettings();
  wireExports();
  wireKeyboard();

  log.emit('info', `Backslide ${VERSION} ready. Nothing has been read from your device yet.`);
}

/* ── Modals ─────────────────────────────────────────────────────────────── */

function renderLimits(): void {
  const list = $<HTMLUListElement>('limits-list');
  list.replaceChildren(...LIMITS_COPY.map((c) => el('li', {}, [renderCopy(c)])));
}

function fillList(id: string, items: readonly string[]): void {
  const ul = document.getElementById(id);
  if (ul) ul.replaceChildren(...items.map((c) => el('li', {}, [renderCopy(c)])));
}

function wireModals(): void {
  const titles: Record<string, string> = {
    how: 'How it works',
    privacy: 'Privacy',
    limits: 'What this cannot tell you',
    about: 'About Backslide',
    keys: 'Keyboard shortcuts',
  };
  document.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-modal]');
    if (!btn) return;
    const which = btn.dataset['modal']!;
    openModal(which, titles[which] ?? '');
  });
}

function openModal(which: string, title: string): void {
  const tpl = document.getElementById(`tpl-${which}`) as HTMLTemplateElement | null;
  if (!tpl) return;
  const body = tpl.content.cloneNode(true) as DocumentFragment;
  modal.show(title, body);
  if (which === 'privacy') {
    fillList('privacy-protected', PRIVACY_PROTECTED);
    fillList('privacy-not', PRIVACY_NOT_PROTECTED);
    fillList('privacy-trust', PRIVACY_TRUST);
  }
  if (which === 'limits') fillList('limits-modal-list', LIMITS_COPY);
}

/* ── Theme ──────────────────────────────────────────────────────────────── */

function wireTheme(): void {
  const stored = localStorage.getItem('backslide.theme');
  if (stored === 'light') document.documentElement.dataset['theme'] = 'light';
  $('theme-toggle').addEventListener('click', () => {
    const light = document.documentElement.dataset['theme'] === 'light';
    if (light) delete document.documentElement.dataset['theme'];
    else document.documentElement.dataset['theme'] = 'light';
    localStorage.setItem('backslide.theme', light ? 'dark' : 'light');
  });
}

/* ── Input ──────────────────────────────────────────────────────────────── */

function wireDropzones(): void {
  for (const side of ['a', 'b'] as const) {
    const zone = $(`dz-${side}`);
    const input = $<HTMLInputElement>(`file-${side}`);
    // A real file input, not only a drop target: iOS Safari cannot drag and drop at all, and a
    // drop-only tool is unusable on a phone.
    $(`pick-${side}`).addEventListener('click', () => input.click());
    input.addEventListener('change', () => {
      const f = input.files?.[0];
      if (f) void loadFile(side, f);
    });
    zone.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('button')) return;
      input.click();
    });
    zone.addEventListener('dragover', (e) => {
      e.preventDefault();
      zone.classList.add('is-dragover');
    });
    zone.addEventListener('dragleave', () => zone.classList.remove('is-dragover'));
    zone.addEventListener('drop', (e) => {
      e.preventDefault();
      zone.classList.remove('is-dragover');
      const f = e.dataTransfer?.files?.[0];
      if (f) void loadFile(side, f);
    });
  }

  $('start-over').addEventListener('click', () => location.reload());
  $('retry').addEventListener('click', () => {
    $('error').setAttribute('hidden', '');
  });
}

/**
 * The one ingestion path. `Try a sample` fetches a file, wraps it in a `File` and calls exactly
 * this — never a shortcut around it — so what the sample demonstrates is what a real upload does.
 */
async function loadFile(side: 'a' | 'b', file: File): Promise<void> {
  try {
    $('error').setAttribute('hidden', '');
    log.emit('info', `Reading ${file.name} as run ${side.toUpperCase()} (${formatCount(file.size)} bytes).`);
    const text = await file.text();
    state[side].name = file.name;
    worker.postMessage({ type: 'load', side, name: file.name, text });
  } catch (err) {
    showError(`Could not read ${file.name}: ${err instanceof Error ? err.message : String(err)}`);
  }
}

function wireSamples(): void {
  const load = async (a: string, b: string, schema: string | null): Promise<void> => {
    try {
      log.emit('info', 'Loading the bundled sample.');
      const [ta, tb] = await Promise.all([
        fetch(a).then((r) => r.text()),
        fetch(b).then((r) => r.text()),
      ]);
      if (schema) {
        const s = await fetch(schema).then((r) => r.text());
        $<HTMLTextAreaElement>('schema-input').value = s;
        state.config = { ...state.config, schema: s };
      }
      await loadFile('a', new File([ta], a.split('/').pop()!, { type: 'application/json' }));
      await loadFile('b', new File([tb], b.split('/').pop()!, { type: 'application/json' }));
    } catch (err) {
      showError(`Could not load the sample: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  $('try-sample').addEventListener('click', (e) => {
    e.stopPropagation();
    void load('/samples/run-a.jsonl', '/samples/run-b.jsonl', '/samples/ticket.schema.json');
  });
  $('try-sample-raw').addEventListener('click', (e) => {
    e.stopPropagation();
    void load('/samples/raw-a.json', '/samples/raw-b.json', null);
  });
}

/* ── Settings ───────────────────────────────────────────────────────────── */

function wireSettings(): void {
  const schema = $<HTMLTextAreaElement>('schema-input');
  let t: ReturnType<typeof setTimeout> | null = null;
  schema.addEventListener('input', () => {
    if (t) clearTimeout(t);
    t = setTimeout(() => {
      state.config = { ...state.config, schema: schema.value.trim() || null };
      run();
    }, 300);
  });

  const nums: [string, (v: number) => void][] = [
    ['th-lat-ratio', (v) => (state.config.thresholds.latencyRatio = v)],
    ['th-lat-floor', (v) => (state.config.thresholds.latencyFloorMs = v)],
    ['th-cost-ratio', (v) => (state.config.thresholds.costRatio = v)],
    ['th-cost-floor', (v) => (state.config.thresholds.costFloor = v)],
  ];
  const th = state.config.thresholds;
  $<HTMLInputElement>('th-lat-ratio').value = String(th.latencyRatio);
  $<HTMLInputElement>('th-lat-floor').value = String(th.latencyFloorMs);
  $<HTMLInputElement>('th-cost-ratio').value = String(th.costRatio);
  $<HTMLInputElement>('th-cost-floor').value = String(th.costFloor);
  for (const [id, set] of nums) {
    $<HTMLInputElement>(id).addEventListener('change', (e) => {
      const v = Number((e.target as HTMLInputElement).value);
      if (Number.isFinite(v)) {
        set(v);
        run();
      }
    });
  }
}

const RULE_LABELS: [keyof NormaliseRules, string, string][] = [
  ['sortObjectKeys', 'Ignore object key order', 'Always safe: JSON objects are unordered.'],
  ['maskUuid', 'Mask UUIDs', 'A fresh uuid per run would make every step differ.'],
  ['maskIso8601', 'Mask timestamps', 'Same shape as UUIDs.'],
  ['maskEpochMs', 'Mask epoch milliseconds', 'Range-guarded to 2001–2033 so real integers survive.'],
  ['maskPrefixedId', 'Mask req_ / trace_ / span_ ids', 'Correlation ids, not content.'],
  ['roundFloat', 'Round floats to 4 places', '0.8300000000000001 and 0.83 are the same number.'],
  ['sortArrays', 'Ignore array order', 'OFF: fine for tags, a real bug for steps or recipients.'],
  ['maskLongHex', 'Mask long hex strings', 'OFF: matches real content hashes.'],
];

function renderRules(): void {
  const grid = $('rule-grid');
  grid.replaceChildren(
    ...RULE_LABELS.map(([key, label, note]) => {
      const input = el('input', { type: 'checkbox' }) as HTMLInputElement;
      input.checked = Boolean(state.config.rules[key]);
      input.addEventListener('change', () => {
        (state.config.rules as unknown as Record<string, boolean>)[key] = input.checked;
        run();
      });
      return el('label', { class: 'checkbox' }, [
        input,
        el('span', {}, [label, el('span', { class: 'field-note' }, [note])]),
      ]);
    }),
    (() => {
      const input = el('input', {
        type: 'text',
        value: state.config.rules.dropKeys.join(', '),
        'aria-label': 'Argument keys to ignore',
      }) as HTMLInputElement;
      input.addEventListener('change', () => {
        state.config.rules.dropKeys = input.value
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const risky = state.config.rules.dropKeys.filter((k) => SUSPICIOUS_DROP_KEYS.has(k));
        if (risky.length > 0) {
          log.emit(
            'warn',
            `Ignoring ${risky.join(', ')} can hide a real change — those keys usually carry meaning, not plumbing.`,
          );
          toast(`Ignoring ${risky.join(', ')} may hide a real regression.`);
        }
        run();
      });
      return el('label', { class: 'field-block' }, [
        el('span', {}, ['Argument keys to ignore entirely']),
        input,
        el('span', { class: 'field-note' }, [
          'The sharpest edge here. Adding a key that carries meaning — priority, status, amount — ' +
            'makes two genuinely different calls look identical and the regression disappears silently.',
        ]),
      ]);
    })(),
  );
}

function renderAlphabet(): void {
  const box = $('alphabet');
  if (state.alphabet.length === 0) {
    box.replaceChildren();
    return;
  }
  const collided = state.alphabet.filter((e) => e.collapsed > 1);
  box.replaceChildren(
    el('h4', {}, [
      `${state.alphabet.length} distinct steps`,
      collided.length > 0 ? ' · ' : '',
      collided.length > 0
        ? el('span', { class: 'is-warn' }, [
            `${collided.length} of them merge more than one real argument set`,
          ])
        : el('span', { class: 'is-ok' }, ['nothing is being merged']),
    ]),
    el(
      'ul',
      { class: 'alphabet-list' },
      state.alphabet.slice(0, 40).map((e) =>
        el('li', { class: e.collapsed > 1 ? 'is-collision' : '' }, [
          el('code', {}, [e.symbol]),
          el('span', { class: 'field-note' }, [
            `${e.uses} call${e.uses === 1 ? '' : 's'}`,
            e.collapsed > 1 ? ` · ${e.collapsed} different argument sets folded together` : '',
          ]),
        ]),
      ),
    ),
  );
}

/* ── Worker messages ────────────────────────────────────────────────────── */

function onWorkerMessage(e: MessageEvent): void {
  const m = e.data;
  switch (m.type) {
    case 'loaded': {
      const s = state[m.side as 'a' | 'b'];
      s.name = m.name;
      s.rows = m.rows;
      s.dialectLabel = m.dialectLabel;
      s.loaded = true;
      $(`dz-${m.side}-title`).textContent = m.name;
      $(`dz-${m.side}-sub`).textContent = `${formatCount(m.rows)} rows · ${m.dialectLabel}`;
      $(`dz-${m.side}`).classList.add('is-loaded');
      log.emit('good', `Run ${String(m.side).toUpperCase()}: ${formatCount(m.rows)} rows, read as ${m.dialectLabel}.`);
      for (const n of m.notes as string[]) log.emit('warn', n);
      if (m.soft) {
        log.emit('warn', `${formatCount(m.rows)} rows is past what Backslide is tested to; it may be slow.`);
      }
      $('start-over').removeAttribute('hidden');
      if (state.a.loaded && state.b.loaded) run();
      break;
    }
    case 'result':
      state.result = { summary: m.summary, cases: m.cases, droppedKeys: m.droppedKeys, schemaError: m.schemaError };
      state.alphabet = m.alphabet;
      state.hasTrajectories = m.hasTrajectories;
      state.dialectLabel = m.dialectLabel;
      renderResult();
      break;
    case 'fieldmap':
      state.config = { ...state.config, fieldMap: m.map as FieldMap };
      log.emit(
        'info',
        `Reading the output from “${m.map.output ?? '?'}”` +
          (m.map.key ? `, joining on “${m.map.key}”.` : ', with no id column to join on.'),
      );
      break;
    case 'warn':
      log.emit('warn', m.message);
      break;
    case 'progress':
      break;
    case 'error':
      showError(m.message);
      break;
    default:
      break;
  }
}

function run(): void {
  if (!state.a.loaded || !state.b.loaded) return;
  worker.postMessage({ type: 'run', config: JSON.parse(JSON.stringify(state.config)) });
}

function showError(message: string): void {
  $('error-text').textContent = message;
  $('error').removeAttribute('hidden');
  log.emit('error', message);
}

/* ── Rendering the result ───────────────────────────────────────────────── */

function renderResult(): void {
  const r = state.result;
  if (!r) return;
  const s = r.summary;
  const h = renderHeadline(s);

  $('result-panel').removeAttribute('hidden');
  $('settings-panel').removeAttribute('hidden');
  $('export-panel').removeAttribute('hidden');
  $('hero').setAttribute('hidden', '');

  const headline = $('headline');
  headline.replaceChildren(el('span', { class: h.blind ? 'big is-blind' : s.regressed > 0 ? 'big is-bad' : 'big is-ok' }, [h.primary]));
  $('headline-sub').textContent = h.secondary ?? '';

  const figures: string[] = [
    `${formatCount(s.cases)} cases`,
    `${formatCount(s.regressed)} worse`,
  ];
  if (s.errored > 0) figures.push(`${formatCount(s.errored)} errored`);
  if (s.diverged > 0) figures.push(`${formatCount(s.diverged)} diverged`);
  if (s.fixed > 0) figures.push(`${formatCount(s.fixed)} better`);
  figures.push(`${formatCount(s.unchanged)} unchanged`);
  if (s.dropped > 0) figures.push(`${formatCount(s.dropped)} dropped`);
  if (s.added > 0) figures.push(`${formatCount(s.added)} added`);
  if (s.ambiguousRows > 0) figures.push(`${formatCount(s.ambiguousRows)} ambiguous`);
  $('figures').textContent = figures.join(' · ');

  const prov = $('provenance');
  prov.replaceChildren(
    el('button', { type: 'button', class: 'glossary-link', 'data-term': 'join-provenance' }, ['Joined']),
    ` on ${s.join.keyFunction} · ${formatCount(s.join.exact)} exact` +
      (s.join.similarity > 0 ? ` · ${formatCount(s.join.similarity)} by similarity` : '') +
      (s.join.ambiguous > 0 ? ` · ${formatCount(s.join.ambiguous)} ambiguous` : ''),
  );

  const notes = $('notes');
  notes.replaceChildren(
    ...s.notes.map((n) => el('div', { class: 'callout is-warn' }, [n])),
    ...(r.schemaError ? [el('div', { class: 'callout is-bad' }, [r.schemaError])] : []),
  );

  renderVerdictStrip(s);
  renderTable();
  renderRules();
  renderAlphabet();
  if (state.hasTrajectories) $('normalise-section').removeAttribute('hidden');
  else $('normalise-section').setAttribute('hidden', '');

  log.emit(
    s.regressed > 0 ? 'warn' : 'good',
    `${h.primary}${h.secondary ? ` ${h.secondary}` : ''}`,
  );
}

function renderVerdictStrip(s: Summary): void {
  const strip = $('verdict-strip');
  const entries = Object.entries(s.flipsByScorer).filter(
    ([id]) => !s.inapplicable.some((i) => i.scorer === id),
  );
  entries.sort((a, b) => b[1] - a[1]);
  strip.replaceChildren(
    el('span', { class: 'strip-label' }, [
      el('button', { type: 'button', class: 'glossary-link', 'data-term': 'precedence' }, [
        'Each row counted once',
      ]),
    ]),
    ...entries.map(([id, n]) =>
      el('span', { class: n > 0 ? 'chip is-hot' : 'chip' }, [`${id} `, el('strong', {}, [String(n)])]),
    ),
  );
  const inap = s.inapplicable;
  $('inapplicable-note').textContent =
    inap.length === 0
      ? ''
      : `Not checked: ${inap.map((i) => `${i.scorer} (${i.why})`).join(' · ')}.`;
}

function visibleCases(): CaseResult[] {
  const r = state.result;
  if (!r) return [];
  const filter = $<HTMLSelectElement>('filter-verdict').value;
  const sort = $<HTMLSelectElement>('sort-by').value;

  let list = [...r.cases];
  if (filter === 'interesting') list = list.filter((c) => INTERESTING.includes(c.verdict));
  else if (filter === 'diverged') list = list.filter((c) => c.verdict.startsWith('diverged'));
  else if (filter !== 'all') list = list.filter((c) => c.verdict === filter);

  if (sort === 'change') {
    list.sort((a, b) => (b.editDistance ?? -1) - (a.editDistance ?? -1));
  } else {
    const order: Verdict[] = [
      'regression',
      'errored',
      'diverged-args',
      'diverged',
      'ambiguous-group',
      'dropped',
      'added',
      'fixed',
      'unmatched',
      'unchanged',
    ];
    list.sort((a, b) => order.indexOf(a.verdict) - order.indexOf(b.verdict) || a.id.localeCompare(b.id));
  }
  return list;
}

function renderTable(): void {
  const body = $<HTMLTableSectionElement>('results-body');
  const list = visibleCases();
  $('row-count').textContent = `${formatCount(list.length)} shown`;

  const frag = document.createDocumentFragment();
  list.forEach((c, i) => {
    const tr = el('tr', { class: `row v-${c.verdict}${i === state.cursor ? ' is-cursor' : ''}`, 'data-id': c.id });
    tr.append(
      el('td', { class: 'case-id' }, [c.id]),
      el('td', {}, [el('span', { class: `verdict v-${c.verdict}` }, [VERDICT_LABEL[c.verdict]])]),
      el('td', { class: 'reason' }, [c.headlineReason]),
      el('td', { class: 'n dim' }, [c.editDistance === null ? '' : formatCount(c.editDistance)]),
      el('td', { class: 'n dim' }, [c.trajectory ? `${c.trajectory.stepsA}/${c.trajectory.stepsB}` : '']),
    );
    tr.addEventListener('click', () => toggleExpand(c.id));
    frag.append(tr);

    if (state.expanded.has(c.id)) frag.append(detailRow(c));
  });
  body.replaceChildren(frag);
}

function detailRow(c: CaseResult): HTMLTableRowElement {
  const td = el('td', { colspan: '5' });
  const box = el('div', { class: 'detail' });

  if (c.trajectory) {
    box.append(
      el('div', { class: 'detail-block' }, [
        el('h4', {}, [
          el('button', { type: 'button', class: 'glossary-link', 'data-term': 'alignment' }, ['Trajectory']),
        ]),
        el('div', { class: 'align' }, [c.trajectory.alignment]),
        el('p', { class: 'field-note' }, [
          c.trajectory.inserted > 0
            ? `${c.trajectory.inserted} step${c.trajectory.inserted === 1 ? '' : 's'} inserted at step ${
                c.trajectory.firstDivergent ?? 0
              }. The rest are identical after normalisation — an extra step is a divergence, not a regression.`
            : c.trajectory.mismatched > 0 || c.trajectory.deleted > 0
              ? `${c.trajectory.mismatched} replaced, ${c.trajectory.deleted} vanished, first at step ${
                  c.trajectory.firstDivergent ?? 0
                }.`
              : c.trajectory.argsDiffer > 0
                ? `${c.trajectory.argsDiffer} step${
                    c.trajectory.argsDiffer === 1 ? '' : 's'
                  } called the same tool with different arguments.`
                : 'Identical after normalisation.',
        ]),
      ]),
    );
  }

  const pre = el('pre', { class: 'diff' });
  for (const part of buildDiff(c.pair.a?.outputText ?? '', c.pair.b?.outputText ?? '')) {
    const value = part.value.length > 4000 ? `${part.value.slice(0, 4000)}…` : part.value;
    if (part.added) pre.append(el('ins', {}, [value]));
    else if (part.removed) pre.append(el('del', {}, [value]));
    else pre.append(value.length > 240 ? `${value.slice(0, 120)}…${value.slice(-120)}` : value);
  }
  box.append(el('div', { class: 'detail-block' }, [el('h4', {}, ['Output']), pre]));

  const scores = c.scores.filter((s) => s.a !== 'na' || s.b !== 'na' || s.detail);
  box.append(
    el('div', { class: 'detail-block' }, [
      el('h4', {}, ['Checks']),
      el(
        'ul',
        { class: 'check-list' },
        scores.map((s) =>
          el('li', { class: s.flipped ? 'is-bad' : s.fixed ? 'is-ok' : '' }, [
            el('code', {}, [s.scorer]),
            ` ${s.a} → ${s.b} — ${s.detail}`,
          ]),
        ),
      ),
    ]),
  );

  box.append(
    el('div', { class: 'detail-block' }, [
      el('h4', {}, ['How these two rows were matched']),
      el('p', { class: 'field-note' }, [
        `${c.pair.method}, ${c.pair.confidence}` +
          (c.pair.score !== null ? `, similarity ${c.pair.score.toFixed(3)}` : '') +
          (c.pair.ambiguousGroupSize
            ? ` — one of ${c.pair.ambiguousGroupSize} rows in this file with the same input`
            : ''),
      ]),
    ]),
  );

  const tr = el('tr', { class: 'detail-row' });
  td.append(box);
  tr.append(td);
  return tr;
}

function toggleExpand(id: string): void {
  if (state.expanded.has(id)) state.expanded.delete(id);
  else state.expanded.add(id);
  renderTable();
}

/* ── Exports ────────────────────────────────────────────────────────────── */

function ctx(): ExportContext {
  return {
    nameA: state.a.name,
    nameB: state.b.name,
    dialect: state.dialectLabel,
    config: state.config,
    generatedAt: new Date().toISOString().slice(0, 19).replace('T', ' ') + ' UTC',
  };
}

function download(name: string, text: string, mime: string): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  log.emit('good', `Saved ${name} (${formatCount(blob.size)} bytes).`);
}

function wireExports(): void {
  $('dl-jsonl').addEventListener('click', () => {
    const r = state.result;
    if (!r) return;
    const strict = $<HTMLInputElement>('ex-strict').checked;
    const side = $<HTMLInputElement>('ex-side-a').checked ? 'a' : 'b';
    const { jsonl, meta } = buildRegressionsJsonl(r, ctx(), { side, strict });
    if (jsonl.length === 0) {
      toast('Nothing got worse, so there is nothing to export.');
      return;
    }
    download('regressions.jsonl', jsonl, 'application/x-ndjson');
    if (meta) download('regressions.meta.jsonl', meta, 'application/x-ndjson');
  });

  $('dl-csv').addEventListener('click', () => {
    const r = state.result;
    if (!r) return;
    download('regression.csv', buildRegressionCsv(r, $<HTMLInputElement>('ex-unchanged').checked), 'text/csv');
  });

  $('dl-md').addEventListener('click', () => {
    const r = state.result;
    if (!r) return;
    download('regression-report.md', buildReportMarkdown(r, ctx()), 'text/markdown');
  });

  $('dl-html').addEventListener('click', () => {
    const r = state.result;
    if (!r) return;
    download('diff-report.html', buildReportHtml(r, ctx()), 'text/html');
  });

  const share = $('share');
  if ('share' in navigator && 'canShare' in navigator) {
    share.removeAttribute('hidden');
    share.addEventListener('click', () => {
      const r = state.result;
      if (!r) return;
      const file = new File([buildReportMarkdown(r, ctx())], 'regression-report.md', { type: 'text/markdown' });
      if (navigator.canShare({ files: [file] })) void navigator.share({ files: [file], title: 'Backslide report' });
      else void navigator.share({ title: 'Backslide', text: renderHeadline(r.summary).primary });
    });
  }

  $('log-copy').addEventListener('click', () => {
    void navigator.clipboard.writeText(log.asText()).then(() => toast('Log copied.'));
  });

  for (const id of ['filter-verdict', 'sort-by']) {
    $(id).addEventListener('change', () => {
      state.cursor = -1;
      renderTable();
    });
  }
}

/* ── Keyboard ───────────────────────────────────────────────────────────── */

function wireKeyboard(): void {
  document.addEventListener('keydown', (e) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    const list = visibleCases();
    switch (e.key) {
      case 'j':
      case 'J':
        state.cursor = Math.min(list.length - 1, state.cursor + 1);
        renderTable();
        scrollCursorIntoView();
        break;
      case 'k':
      case 'K':
        state.cursor = Math.max(0, state.cursor - 1);
        renderTable();
        scrollCursorIntoView();
        break;
      case 'Enter': {
        const c = list[state.cursor];
        if (c) toggleExpand(c.id);
        break;
      }
      case 'e':
      case 'E':
        $('export-panel').scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      case 'l':
      case 'L':
        $('log-toggle').click();
        break;
      case '?':
        openModal('keys', 'Keyboard shortcuts');
        break;
      default:
        break;
    }
  });
}

function scrollCursorIntoView(): void {
  document.querySelector('.row.is-cursor')?.scrollIntoView({ block: 'nearest' });
}

boot();
